/*******************************************************
 * Copyright (C) BoostCommerce.net
 *
 * This file is part of commercial BoostCommerce.net projects.
 *
 * This file can not be copied and/or distributed without the express
 * permission of BoostCommerce.net
 *
 * @Date:   2021-01-10 00:00:00
 *
 *******************************************************/
// @flow

import _ from 'lodash';

import { SubmissionError } from 'redux-form';

/**
 * Build initial values
 * @param {*} settings
 * @param {*} optionsList
 * @param {*} i18n
 * @returns
 */
export const buildInitialVariantsDisplayValues = (
  settings,
  optionsList,
  i18n,
  merchandisingV2,
  hasAddon,
  subscriptionV3
) => {
  const {
    enable = false,
    collections: inputCollections = [],
    options: inputOptions = [],
    appliesTo = {}
  } = settings;

  // Collections
  const collections = _.find(inputCollections, { valueType: 'all' })
    ? [
        {
          value: 'all',
          label: i18n.translate('AdminHTML.VariantsDisplay.Form.collectionSelectAll'),
          valueType: 'all'
        }
      ]
    : _.map(inputCollections, collection => ({
        value: collection.id,
        label: collection.title,
        valueType: 'specific'
      }));

  // Options
  const options = _.find(inputOptions, { valueType: 'all' })
    ? [
        {
          value: 'all',
          label: i18n.translate('AdminHTML.VariantsDisplay.Form.optionSelectAll'),
          valueType: 'all'
        }
      ]
    : _.map(inputOptions, option => {
        const originalInfo = _.find(optionsList, { name: option.optionName }) || {};

        return {
          value: option.optionName,
          label: originalInfo.originalName,
          valueType: 'specific'
        };
      });

  const appliesToInstantSearchWidget = merchandisingV2
    ? false
    : appliesTo.instantSearchWidget || false;

  return {
    enable: hasAddon || !subscriptionV3 ? enable : false,
    options,
    collections,
    appliesToCollectionPage: appliesTo.collectionPage || true,
    appliesToInstantSearchWidget,
    appliesToSearchPage: appliesTo.searchPage || false
  };
};

/**
 * Build list of collections
 * @param {*} collections
 * @param {*} i18n
 * @returns
 */
export const buildCollections = (collections, enableSelectAllCollection, i18n) => {
  const list = _.map(collections, item => {
    item.disabled = enableSelectAllCollection;
    return item;
  });

  // Add select all collections on top of list
  list.splice(0, 0, {
    value: 'all',
    label: i18n.translate('AdminHTML.VariantsDisplay.Form.collectionSelectAll'),
    valueType: 'all'
  });

  return list;
};

/**
 * Build list of options
 * @param {*} optionsList
 * @param {*} i18n
 * @returns
 */
export const buildOptions = (optionsList, enableSelectAllOption, i18n) => {
  let list = _.map(optionsList, item => ({
    value: item.name,
    label: item.originalName,
    valueType: 'specific',
    disabled: enableSelectAllOption
  }));

  // Add Select all options
  list.splice(0, 0, {
    value: 'all',
    label: i18n.translate('AdminHTML.VariantsDisplay.Form.optionSelectAll'),
    valueType: 'all'
  });

  return list;
};

/**
 * Validate error before saving
 * @param {*} data
 */
export const validateVariantDisplayValues = (data, i18n) => {
  const { collections = [], options = [] } = data;

  const error = {};
  if (_.isEmpty(options)) {
    error.options = i18n.translate('AdminHTML.VariantsDisplay.Validation.selectAtLeastOneOption');
  }

  if (_.isEmpty(collections)) {
    error.collections = i18n.translate(
      'AdminHTML.VariantsDisplay.Validation.selectAtLeastOneCollection'
    );
  }

  if (!_.isEmpty(error)) {
    throw new SubmissionError(error);
  }
};

/**
 * Build save data
 * @param {*} data
 * @returns
 */
export const buildVariantsDisplayValues = (data, merchandisingV2) => {
  const {
    enable,
    collections: dataCollections = [],
    options: dataOptions = [],
    appliesToSearchPage = false,
    appliesToInstantSearchWidget = false
  } = data;

  const collections = _.find(dataCollections, { valueType: 'all' })
    ? [{ valueType: 'all' }]
    : _.map(dataCollections, collection => {
        return {
          id: collection.value,
          title: collection.label,
          valueType: 'specific'
        };
      });

  const options = _.find(dataOptions, { valueType: 'all' })
    ? [{ valueType: 'all' }]
    : _.map(dataOptions, option => {
        return {
          optionName: option.value,
          valueType: 'specific'
        };
      });

  const appliesToISW = merchandisingV2 ? false : appliesToInstantSearchWidget;

  return {
    enable,
    collections,
    options,
    appliesTo: {
      collectionPage: true,
      instantSearchWidget: appliesToISW,
      searchPage: appliesToSearchPage
    }
  };
};
